/**
 * The tool catalogue — light half.
 *
 * SPLIT FROM THE FULL CONTENT DELIBERATELY. Every landing page used to ship
 * the prose for every tool page: measured at 76% of the entry chunk,
 * which had reached 29.3 KB against a 30 KB budget. A page carrying
 * every other page's copy in order to render its own is the opposite of
 * the content-first structure plan §12.2 is built around, and it was about to
 * fail the gate outright.
 *
 * This file holds only what the CLIENT genuinely needs everywhere: the footer
 * list, the home grid, the page head, and routing. The prose lives in
 * `details.ts`, which the client never loads — the prerender embeds the
 * current page's detail into its HTML instead.
 */

export interface CatalogueEntry {
  /** URL slug: /merge-pdf */
  readonly slug: string;
  /** Registry id the editor opens with, or null when the tool is not built. */
  /**
   * The registered tool this page opens.
   *
   * `string`, not `string | null`. The nullable form existed to express "not
   * built yet", no entry has ever used it, and it kept four unreachable
   * branches alive across three components. A waiting list, if one is wanted
   * again, should be a real list rather than a null every page has to test for.
   */
  readonly toolId: string;
  readonly title: string;
  /** <title> and the H1 respectively — the H1 is allowed to be friendlier. */
  readonly metaTitle: string;
  readonly heading: string;
  readonly metaDescription: string;
  /**
   * Which opener this page shows. 'images' pages convert what is dropped into
   * a PDF before handing it to the editor, so everything downstream works on
   * an ordinary document with no special cases.
   */
  readonly opener?: 'images';
}

/** Shared closing note. The only text reused across pages, and deliberately so. */
export const PRIVACY_NOTE =
  'Your file is opened by this page and processed by your own browser. It is never uploaded, so there is nothing for us to keep, leak or hand over.';

export const TOOL_PAGES: readonly CatalogueEntry[] = [
  {
    slug: 'merge-pdf',
    toolId: 'organize',
    title: 'Merge PDF',
    metaTitle: 'Merge PDF files in your browser — nothing uploaded',
    heading: 'Merge PDF files',
    metaDescription:
      'Combine several PDFs into one, in your browser. No upload, no size limit, no watermark. Open the files and drag pages into the order you want.',
  },

  {
    slug: 'split-pdf',
    toolId: 'split',
    title: 'Split PDF',
    metaTitle: 'Split a PDF in your browser — no upload required',
    heading: 'Split a PDF',
    metaDescription:
      'Cut a PDF into parts, keeping exactly the pages you choose. Runs in your browser, so the document is never uploaded.',
  },

  {
    slug: 'organize-pdf',
    toolId: 'organize',
    title: 'Organize PDF',
    metaTitle: 'Reorder and rotate PDF pages — in your browser',
    heading: 'Organize PDF pages',
    metaDescription:
      'Reorder, rotate and remove PDF pages by dragging them. Everything happens in your browser; the file is never uploaded.',
  },

  {
    slug: 'rotate-pdf',
    toolId: 'rotate',
    title: 'Rotate PDF',
    metaTitle: 'Rotate PDF pages permanently — no upload',
    heading: 'Rotate PDF pages',
    metaDescription:
      'Turn PDF pages a quarter, half or three-quarter turn and save the result. Runs entirely in your browser.',
  },

  {
    slug: 'remove-pages',
    toolId: 'remove',
    title: 'Delete PDF pages',
    metaTitle: 'Delete pages from a PDF — in your browser',
    heading: 'Delete pages from a PDF',
    metaDescription:
      'Remove pages from a PDF without uploading it. Removed pages stay recoverable until you export.',
  },

  {
    slug: 'extract-pages',
    toolId: 'extract',
    title: 'Extract PDF pages',
    metaTitle: 'Extract pages from a PDF — nothing uploaded',
    heading: 'Extract pages from a PDF',
    metaDescription:
      'Pull specific pages out of a PDF into a new document, in your browser. No upload, no account.',
  },

  {
    slug: 'watermark-pdf',
    toolId: 'watermark',
    title: 'Watermark PDF',
    metaTitle: 'Add a watermark to a PDF — free, no upload',
    heading: 'Add a watermark to a PDF',
    metaDescription:
      'Stamp DRAFT, CONFIDENTIAL or your own text across every page. Drag it into position and see the result live.',
  },

  {
    slug: 'add-page-numbers',
    toolId: 'page-numbers',
    title: 'Add page numbers to PDF',
    metaTitle: 'Add page numbers to a PDF — in your browser',
    heading: 'Add page numbers to a PDF',
    metaDescription:
      'Number the pages of a PDF, with roman or alphabetic numbering and a custom format. Renumbers automatically as you reorder.',
  },

  {
    slug: 'headers-footers',
    toolId: 'headers-footers',
    title: 'Add headers and footers to PDF',
    metaTitle: 'Add a header or footer to a PDF — no upload',
    heading: 'Add headers and footers',
    metaDescription:
      'Put repeating text at the top or bottom of every page of a PDF, in your browser.',
  },

  {
    slug: 'sign-pdf',
    toolId: 'signature',
    title: 'Sign a PDF',
    metaTitle: 'Add a signature to a PDF — in your browser',
    heading: 'Add a signature to a PDF',
    metaDescription:
      'Place a visual signature on a PDF and position it exactly. Runs in your browser; the document is never uploaded.',
  },

  {
    slug: 'protect-pdf',
    toolId: 'protect',
    title: 'Password protect PDF',
    metaTitle: 'Password protect a PDF with AES-256 — no upload',
    heading: 'Password protect a PDF',
    metaDescription:
      'Encrypt a PDF with AES-256 and control printing, copying and editing. The password never leaves your browser.',
  },

  {
    slug: 'unlock-pdf',
    toolId: 'unlock',
    title: 'Unlock PDF',
    metaTitle: 'Remove a password from a PDF you can open',
    heading: 'Remove PDF protection',
    metaDescription:
      'Strip password protection and restrictions from a PDF you are able to open, in your browser.',
  },

  {
    slug: 'redact-pdf',
    toolId: 'redact',
    title: 'Redact a PDF',
    metaTitle: 'Redact a PDF properly — the text is removed, not covered',
    metaDescription:
      'Find and permanently remove text from a PDF: emails, card numbers, national IDs, or any exact phrase. The content is destroyed and the result verified. Nothing uploaded.',
    heading: 'Redact a PDF properly',
  },

  {
    slug: 'bates-numbering',
    toolId: 'bates',
    title: 'Bates numbering',
    metaTitle: 'Add Bates numbers to a PDF — free, in your browser',
    metaDescription:
      'Stamp sequential Bates numbers on every page of a PDF with a custom prefix, padding and starting number. Runs in your browser; nothing is uploaded.',
    heading: 'Add Bates numbers to a PDF',
  },

  {
    slug: 'find-hidden-text',
    toolId: 'hidden-content',
    title: 'Find hidden text',
    metaTitle: 'Find hidden text in a PDF — white-on-white, covered, off-page',
    metaDescription:
      'Scan a PDF for text that is in the file but not on the screen: white on white, transparent, under an image, off the page, or too small to read. Nothing is uploaded.',
    heading: 'Find text hidden in a PDF',
  },

  {
    slug: 'sanitize-pdf',
    toolId: 'sanitize',
    title: 'Sanitize a PDF',
    metaTitle: 'Remove metadata, scripts and attachments from a PDF',
    metaDescription:
      'See what a PDF is carrying — document properties, embedded files, scripts, annotations — and strip what you do not want to send. Runs in your browser.',
    heading: 'Strip what a PDF is carrying',
  },

  {
    slug: 'inspect-pdf',
    toolId: 'xray',
    title: 'Inspect a PDF',
    metaTitle: "See what is inside a PDF — fonts, images, scripts, hidden files",
    heading: 'See what is actually inside a PDF',
    metaDescription:
      'Read a PDF’s contents: every font, every image and its real resolution, embedded files, JavaScript, metadata and why the file is the size it is. Nothing is uploaded.',
  },

  {
    slug: 'repair-pdf',
    toolId: 'repair',
    title: 'Repair PDF',
    metaTitle: 'Repair a damaged PDF — in your browser',
    heading: 'Repair a damaged PDF',
    metaDescription:
      'Rebuild a PDF that other readers refuse to open, without uploading it anywhere.',
  },

  {
    slug: 'fast-web-view',
    toolId: 'linearize',
    title: 'Optimise PDF for web',
    metaTitle: 'Linearize a PDF for fast web view — no upload',
    heading: 'Optimise a PDF for the web',
    metaDescription:
      'Reorder a PDF so browsers can show the first page before the rest downloads. Content is unchanged.',
  },

  {
    slug: 'compress-pdf',
    toolId: 'compress',
    title: 'Compress PDF',
    metaTitle: 'Compress a PDF in your browser — with the actual numbers',
    heading: 'Compress a PDF',
    metaDescription:
      'Make a PDF smaller without uploading it. Recompresses streams and converts images to JPEG where that helps. Published benchmark: 91% on documents with lossless images, near zero on already-optimised ones.',
  },

  {
    slug: 'edit-pdf-metadata',
    toolId: 'metadata',
    title: 'Edit PDF properties',
    metaTitle: 'Change a PDF’s title and author — in your browser',
    heading: 'Change what a PDF says about itself',
    metaDescription:
      'Set or replace a PDF’s title, author, subject and keywords. The original properties are dropped either way. Nothing is uploaded.',
  },

  {
    slug: 'crop-pdf',
    toolId: 'crop',
    title: 'Crop PDF',
    metaTitle: 'Crop PDF margins in your browser — no upload',
    heading: 'Crop the margins off a PDF',
    metaDescription:
      'Trim margins from every page of a PDF at once, with a live preview. Runs in your browser; the file is never uploaded.',
  },

  {
    slug: 'sign-pdf-with-image',
    toolId: 'stamp-image',
    title: 'Image stamp',
    metaTitle: 'Add a signature image, logo or stamp to a PDF',
    heading: 'Stamp an image onto a PDF',
    metaDescription:
      'Place a scanned signature, a company logo or an image watermark on a PDF and position it exactly. Runs in your browser; nothing is uploaded.',
  },

  {
    slug: 'extract-text-from-pdf',
    toolId: 'extract-text',
    title: 'Extract text',
    metaTitle: 'Extract text from a PDF — in your browser, no upload',
    heading: 'Pull the text out of a PDF',
    metaDescription:
      'Get a PDF’s text as a plain text file, page by page. Runs in your browser; the document is never uploaded.',
  },

  {
    slug: 'pdf-to-image',
    toolId: 'pdf-to-image',
    title: 'PDF to image',
    metaTitle: 'Convert PDF to PNG or JPG — in your browser',
    heading: 'Turn PDF pages into images',
    metaDescription:
      'Convert every page of a PDF to PNG or JPEG at the resolution you choose. Multiple pages come back as one zip. Nothing is uploaded.',
  },

  {
    slug: 'jpg-to-pdf',
    toolId: 'organize',
    opener: 'images',
    title: 'JPG to PDF',
    metaTitle: 'Convert JPG or PNG images to a PDF — in your browser',
    heading: 'Turn images into a PDF',
    metaDescription:
      'Combine JPG and PNG images into a single PDF, at the page size you choose. Runs in your browser; nothing is uploaded.',
  },

  {
    slug: 'search-pdf',
    toolId: 'find',
    title: 'Search a PDF',
    metaTitle: 'Search inside a PDF — every match highlighted, no upload',
    heading: 'Search inside a PDF',
    metaDescription: 'Find every occurrence of a word, phrase or pattern in a PDF, highlighted on the pages and counted per page. Runs in your browser.',
  },

  {
    slug: 'auto-crop-pdf',
    toolId: 'auto-crop',
    title: 'Auto-crop',
    metaTitle: 'Automatically crop PDF margins — in your browser',
    heading: 'Crop a PDF to its content',
    metaDescription: 'Trim every page down to the content on it automatically, page by page. Ideal for scans with uneven borders. Nothing is uploaded.',
  },

  {
    slug: 'pages-per-sheet',
    toolId: 'n-up',
    title: 'Pages per sheet',
    metaTitle: 'Put multiple PDF pages on one sheet — 2-up, 4-up, 9-up',
    heading: 'Put several pages on one sheet',
    metaDescription: 'Lay 2, 4, 6, 9 or 16 PDF pages onto each sheet for handouts, proofs and printing. Runs in your browser; nothing is uploaded.',
  },

  {
    slug: 'compare-pdf',
    toolId: 'compare',
    title: 'Compare PDFs',
    metaTitle: 'Compare two PDFs and see what changed — no upload',
    heading: 'Compare two versions of a document',
    metaDescription: 'See exactly which words changed between two versions of a PDF, page by page. Both files stay on your computer.',
  },

  {
    slug: 'pdf-bookmarks',
    toolId: 'bookmarks',
    title: 'Edit bookmarks',
    metaTitle: 'Keep PDF bookmarks through a merge — or build them',
    heading: 'Bookmarks that survive editing',
    metaDescription: 'Merge PDFs without losing the outline, or build bookmarks from the pages. Runs in your browser; nothing is uploaded.',
  },

  {
    slug: 'smart-split-pdf',
    toolId: 'smart-split',
    title: 'Smart split',
    metaTitle: 'Split a PDF at bookmarks, blank pages or a phrase',
    heading: 'Split one PDF into many, automatically',
    metaDescription: 'Split a PDF at every bookmark, blank separator page, or page containing a phrase. Get a zip of real PDFs. Nothing is uploaded.',
  },

  {
    slug: 'annotate-pdf',
    toolId: 'annotate',
    title: 'Annotate a PDF',
    metaTitle: 'Highlight, draw and mark up a PDF — nothing uploaded',
    heading: 'Mark up a PDF',
    metaDescription: 'Highlight text, draw boxes and arrows, write freehand on a PDF. Marks are drawn into the page, so they print everywhere. Nothing is uploaded.',
  },

  {
    slug: 'fill-pdf-form',
    toolId: 'fill-form',
    title: 'Fill in a PDF form',
    metaTitle: 'Fill in a PDF form and lock the answers — no upload',
    heading: 'Fill in a PDF form',
    metaDescription: 'Type into a PDF\'s fields and export a locked copy nobody can edit. The form never leaves your computer.',
  },

  {
    slug: 'verify-pdf-signature',
    toolId: 'verify-signatures',
    title: 'Verify signatures',
    metaTitle: 'Check whether a signed PDF has been altered — no upload',
    heading: 'Has this signed PDF been changed?',
    metaDescription: 'Check a digitally signed PDF for changes made after signing, including content appended afterwards. Runs entirely in your browser.',
  },

  {
    slug: 'ocr-pdf',
    toolId: 'ocr',
    title: 'Make a scan searchable',
    metaTitle: 'OCR a PDF in your browser — the scan never leaves it',
    heading: 'Make a scanned PDF searchable',
    metaDescription: 'Recognise the text in a scanned PDF and make it searchable and selectable, without changing how the page looks. Runs entirely on your machine.',
  },

  {
    slug: 'clean-up-scan',
    toolId: 'clean-scan',
    title: 'Clean up a scan',
    metaTitle: 'Straighten and de-speckle a scanned PDF — in your browser',
    heading: 'Straighten and clean a scanned PDF',
    metaDescription: 'Deskew crooked scans, remove specks and dust, and brighten a grey photocopy. Runs on your own machine; nothing is uploaded.',
  },

  {
    slug: 'remove-blank-pages',
    toolId: 'remove-blank',
    title: 'Remove blank pages',
    metaTitle: 'Remove blank pages from a PDF — in your browser',
    heading: 'Remove blank pages from a PDF',
    metaDescription:
      'Find and drop the empty sheets a double-sided scan leaves behind, including scanned blanks. Runs in your browser, so the document is never uploaded.',
  },

  {
    slug: 'combine-odd-even-pages',
    toolId: 'interleave',
    title: 'Alternate odd and even pages',
    metaTitle: 'Combine front and back scans into one PDF — no upload',
    heading: 'Combine a scan of the fronts and a scan of the backs',
    metaDescription:
      'Scanned the odd pages, then the even ones? Interleave the two PDFs one page from each, with the reversed back stack handled for you. Runs in your browser.',
  },

  {
    slug: 'add-letterhead-to-pdf',
    toolId: 'overlay-pdf',
    title: 'Add letterhead',
    metaTitle: 'Add a letterhead or background PDF to every page — no upload',
    heading: 'Add a letterhead to a PDF',
    metaDescription:
      'Put your letterhead, background or template PDF behind or on top of every page, kept as sharp vector content. Runs in your browser; nothing is uploaded.',
  },

  {
    slug: 'resize-pdf',
    toolId: 'resize',
    title: 'Resize PDF pages',
    metaTitle: 'Resize PDF pages to A4 or Letter — in your browser',
    heading: 'Resize PDF pages to A4 or Letter',
    metaDescription:
      'Put every page of a mixed-size PDF on one paper size before printing, scaled to fit and never cropped. Runs in your browser, so nothing is uploaded.',
  },

  {
    slug: 'split-pdf-by-size',
    toolId: 'split-size',
    title: 'Split PDF by size',
    metaTitle: 'Split a PDF into parts under a file size — no upload',
    heading: 'Split a PDF by file size',
    metaDescription:
      'Cut a large PDF into parts that each stay under a size limit, for email and upload forms. Every part is measured. Runs in your browser.',
  },

  {
    slug: 'flatten-pdf',
    toolId: 'flatten',
    title: 'Flatten PDF',
    metaTitle: 'Flatten a PDF — lock forms and comments, no upload',
    heading: 'Flatten a PDF',
    metaDescription:
      'Lock form answers and burn comments, highlights and stamps into the page so they cannot be edited or removed. Runs in your browser; nothing is uploaded.',
  },

  {
    slug: 'pdf-table-to-csv',
    toolId: 'table-to-csv',
    title: 'Table to CSV',
    metaTitle: 'Extract a table from a PDF to CSV — no upload',
    heading: 'Get a table out of a PDF',
    metaDescription: 'Pull ruled tables out of a PDF as CSV files you can open in a spreadsheet. Reads the document\'s own ruling lines. Nothing is uploaded.',
  },

  {
    slug: 'pdf-to-word',
    toolId: 'pdf-to-word',
    title: 'PDF to Word',
    metaTitle: 'PDF to Word (DOCX) in your browser — no upload',
    heading: 'Convert a PDF to Word',
    metaDescription:
      'Turn a PDF into an editable .docx with its paragraphs and headings rebuilt as real text. Runs in your browser, so the document is never uploaded.',
  },

  {
    slug: 'pdf-to-excel',
    toolId: 'pdf-to-excel',
    title: 'PDF to Excel',
    metaTitle: 'PDF to Excel (XLSX) — ruled tables, no upload',
    heading: 'Convert a PDF table to Excel',
    metaDescription:
      'Turn ruled tables in a PDF into an .xlsx workbook, one sheet per table, with numbers stored as numbers. Runs in your browser; nothing is uploaded.',
  },

  {
    slug: 'pdf-to-powerpoint',
    toolId: 'pdf-to-powerpoint',
    title: 'PDF to PowerPoint',
    metaTitle: 'PDF to PowerPoint (PPTX) — one slide a page, no upload',
    heading: 'Convert a PDF to PowerPoint',
    metaDescription:
      'Turn each PDF page into a slide you can present and annotate over, as a .pptx. Runs in your browser, so the file is never uploaded.',
  },

  {
    slug: 'extract-images-from-pdf',
    toolId: 'extract-images',
    title: 'Extract images',
    metaTitle: 'Extract images from a PDF at full resolution — no upload',
    heading: 'Extract the images from a PDF',
    metaDescription:
      'Pull the embedded images out of a PDF at the resolution they were stored, with JPEGs handed back as the original file. Runs in your browser.',
  },

  {
    slug: 'pdf-to-markdown',
    toolId: 'pdf-to-markdown',
    title: 'PDF to Markdown',
    metaTitle: 'PDF to Markdown — for feeding documents to an LLM',
    heading: 'Convert a PDF to Markdown',
    metaDescription:
      'Convert a PDF to clean Markdown with its headings and tables intact, ready to paste into a wiki or feed to a language model. Nothing is uploaded.',
  },

  {
    slug: 'create-pdf-booklet',
    toolId: 'booklet',
    title: 'Make a booklet',
    metaTitle: 'Make a PDF booklet — saddle-stitch imposition, no upload',
    heading: 'Turn a PDF into a booklet',
    metaDescription:
      'Reorder a PDF so that printing double-sided, folding and stapling gives a booklet that reads in order. Runs in your browser; nothing is uploaded.',
  },

  {
    slug: 'check-pdf-accessibility',
    toolId: 'check-accessibility',
    title: 'Check accessibility',
    metaTitle: 'Check PDF accessibility in your browser — no upload',
    heading: 'Check a PDF for accessibility',
    metaDescription:
      'Check whether a PDF is tagged, titled, language-marked and described for screen readers, and download the report. Your document is never uploaded.',
  },

  {
    slug: 'extract-pdf-attachments',
    toolId: 'extract-attachments',
    title: 'Extract attachments',
    metaTitle: 'Extract files embedded in a PDF — no upload',
    heading: 'Get the files embedded in a PDF',
    metaDescription:
      'Pull out the files hidden inside a PDF, including the XML in an electronic invoice. Runs in your browser, so nothing is uploaded.',
  },

  {
    slug: 'remove-images-from-pdf',
    toolId: 'remove-images',
    title: 'Remove images',
    metaTitle: 'Remove images from a PDF — shrink it, keep the text',
    heading: 'Remove the images from a PDF',
    metaDescription:
      'Strip the pictures out of a PDF to make it small enough to email, keeping the text and layout. The images cannot be recovered. No upload.',
  },

  {
    slug: 'add-blank-pages-to-pdf',
    toolId: 'insert-blanks',
    title: 'Add blank pages',
    metaTitle: 'Add blank pages to a PDF for double-sided printing',
    heading: 'Add blank pages to a PDF',
    metaDescription:
      'Pad a PDF with blank pages so double-sided printing collates properly, or put a blank after every page. Runs in your browser.',
  },

  {
    slug: 'check-pdf-links',
    toolId: 'check-links',
    title: 'Check links',
    metaTitle: 'Check the links in a PDF — spot phishing, no upload',
    heading: 'Check the links in a PDF',
    metaDescription:
      'See where every link in a PDF really goes, and catch links that show one site but lead to another. Nothing is visited and nothing is uploaded.',
  }
];

/**
 * Pages for tools that do not exist yet.
 *
 * Empty today. The shape stays because it is load bearing: `Home` renders
 * these separately as "in development", and a page promising a tool the editor
 * cannot open must never sit in `TOOL_PAGES` — the footer counts that array as
 * "tools available".
 */
/**
 * There is no "coming soon" list, and there has not been for some time.
 *
 * `UPCOMING` was an empty array spread into `ALL_PAGES`, and no entry anywhere
 * has `toolId: null` -- so Home's "In development" block, ToolPage's "Not
 * available yet" card and both false branches in Layout were unreachable, and
 * `CatalogueEntry.toolId` was typed `string | null` solely to describe a state
 * that cannot occur. Reinstating a waiting list means adding a real one, not
 * keeping an empty one alive against the possibility.
 */
export const ALL_PAGES: readonly CatalogueEntry[] = TOOL_PAGES;

export const pageBySlug = (slug: string): CatalogueEntry | undefined =>
  ALL_PAGES.find((p) => p.slug === slug);

export const ALL_SLUGS: readonly string[] = ALL_PAGES.map((p) => p.slug);
